import "./tableView.scss";

import { List } from "immutable";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { immutableRecords, PropTypeShapes } from "../../types";
import { withContainerContext } from "../container/ContainerContext";
import withScrollableView from "../scrollable/withScrollableView";
import SplitView from "../splitView";
import TableBodyView from "./TableBodyView";
import TableHeaderView from "./TableHeaderView";

// import debugUtils from "../../utils/debugUtils";

class TableCanvasView extends PureComponent {
  constructor(props, context) {
    super(props, context);
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("TableCanvasView", this.props, nextProps, true);
  // }

  renderHeader() {
    return (
      <TableHeaderView
        key="tableHeaderScrollColumn"
        height={this.props.data.headerHeight}
        columns={this.props.data.columns}
        tableId={this.props.tableId}
        viewId={`${this.props.viewId}-scrollHeader`}
        lockedColumnCount={this.props.lockedColumnCount}
        allowColumnResize={this.props.allowColumnResize}
        onResizeColumn={this.props.onResizeColumn}
        allowColumnReorder={this.props.allowColumnReorder}
        onLiveColumnDrag={this.props.onLiveColumnDrag}
        onColumnDrop={this.props.onColumnDrop}
        allowColumnSelect={false}
        showGutter={true}
        onToggleColumnPopover={this.props.onToggleColumnPopover}
        allowSort={this.props.allowSort}
        sortableColumnIds={this.props.sortableColumnIds}
        sortByColumnId={this.props.sortByColumnId}
        sortInDesc={this.props.sortInDesc}
        onSortByColumn={this.props.onSortByColumn}
        fontSize={this.props.fontSize}
      />
    );
  }

  renderBody() {
    return (
      <TableBodyView
        onFocusIn={this.props.onFocusIn}
        onFocusOut={this.props.onFocusOut}
        key="tableBodyView"
        {...this.props}
      />
    );
  }

  render() {
    const splitProps = {
      vertical: false,
      constraints: List([
        immutableRecords.SplitConstraintsRecord({
          minSize: this.props.data.headerHeight,
          maxSize: this.props.data.headerHeight,
          preferredSize: this.props.data.headerHeight,
          defaultSize: this.props.data.headerHeight,
          canResize: false,
          hasDivider: false,
          dividerVisible: false
        }),
        immutableRecords.SplitConstraintsRecord({
          preferredSize: this.props.containerRect.height - this.props.data.headerHeight,
          canResize: false,
          hasDivider: false,
          dividerVisible: false
        })
      ])
    };

    const childComponents = [this.renderHeader(), this.renderBody()];

    return (
      <SplitView key="tableCanvasSplitView" {...splitProps}>
        {childComponents}
      </SplitView>
    );
  }
}

TableCanvasView.propTypes = {
  // Data from Table
  viewId: PropTypes.string.isRequired,
  tableId: PropTypes.string.isRequired,
  data: PropTypeShapes.tableData.isRequired,

  // What can the header do?
  availableColumns: PropTypeShapes.availableTableColumnList,
  allowColumnSelect: PropTypes.bool,
  allowColumnResize: PropTypes.bool,
  allowColumnReorder: PropTypes.bool,
  onToggleColumnPopover: PropTypes.func,
  onToggleColumn: PropTypes.func,
  onResizeColumn: PropTypes.func,
  onLiveColumnDrag: PropTypes.func,
  onColumnDrop: PropTypes.func,

  // Can the user sort the table?
  allowSort: PropTypes.bool,
  sortableColumnIds: ImmutablePropTypes.listOf(PropTypes.string),
  sortByColumnId: PropTypes.string,
  sortInDesc: PropTypes.bool,
  onSortByColumn: PropTypes.func,

  // How many columns on the left are locked in place?
  lockedColumnCount: PropTypes.number.isRequired,

  // Which cells are selected?
  selectedRowIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  cellSelection: PropTypes.instanceOf(immutableRecords.TableCellSelectionRecord).isRequired,
  focusedCell: PropTypes.instanceOf(immutableRecords.TableCellPositionRecord),

  // Do we need to draw any drop locators...?
  drawDropRow: PropTypeShapes.tableRow,
  drawDropAction: PropTypes.string,

  // Used for DatePicker
  calendar: PropTypeShapes.calendar,
  autoAdjustEndDate: PropTypes.bool,

  // Registering cells with TableView
  registerCellFocusCallback: PropTypes.func.isRequired,
  unregisterCellFocusCallback: PropTypes.func.isRequired,

  // Used for dependencies
  getCellOptions: PropTypes.func,

  // Called whenever a user clicks to toggle a parent.
  // Expect the view to go and reload the remaining data...?
  onToggleChildren: PropTypes.func.isRequired,
  // Called whenever a value is changed for a cell
  onValueChanged: PropTypes.func.isRequired,
  onMultiValueChanged: PropTypes.func.isRequired,

  // And internal table functions
  onFocusIn: PropTypes.func.isRequired,
  onFocusOut: PropTypes.func.isRequired,
  moveFocus: PropTypes.func.isRequired,
  getCellId: PropTypes.func.isRequired,
  onCellClick: PropTypes.func.isRequired,
  onCellDoubleClick: PropTypes.func.isRequired,
  onCellPopoverToggle: PropTypes.func.isRequired,
  onCellCut: PropTypes.func.isRequired,
  onCellCopy: PropTypes.func.isRequired,
  onCellPaste: PropTypes.func.isRequired,
  onCellDelete: PropTypes.func.isRequired,
  onRowGutterClick: PropTypes.func,

  // Can the user drag rows
  allowRowDrag: PropTypes.bool,
  // Can the user drag rows to create nested groups.
  // If Yes, indicators will be used to show where an item should drop.
  // If No, rows will move out of the way and leave a gap where they belong.
  allowRowDragNesting: PropTypes.bool,
  // What rows are currently being dragged...?
  draggedRowIds: ImmutablePropTypes.list,

  // Does this group of columns show the gutter...?
  showRowGutter: PropTypes.bool,
  rowGutterIcon: PropTypes.string,
  rowGutterIconColor: PropTypes.string,

  // Dragging rows
  onDragLeave: PropTypes.func,
  onDragEnter: PropTypes.func,
  onLiveRowDrag: PropTypes.func,
  onRowDrop: PropTypes.func,

  canItemDropOnRow: PropTypes.func.isRequired,
  canRowDrop: PropTypes.bool.isRequired,
  canTypeHaveSuperType: PropTypes.func.isRequired,
  requestCanDropItems: PropTypes.func.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool,
  displayItems: ImmutablePropTypes.map,
  showViolatedConstraints: PropTypes.bool,
  onShowInspector: PropTypes.func,
  disableReordering: PropTypes.bool,
  reorderSortByColumn: PropTypes.string,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  fontSize: PropTypes.number.isRequired,
  pulsateIconId: PropTypes.string
};

const scrollableViewOptions = {
  scrollHorizontally: true,
  scrollVertically: false,
  showHorizontalScrollbar: true,
  showVerticalScrollbar: true
};

export default withScrollableView(withContainerContext(TableCanvasView), scrollableViewOptions);
